import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Clock, Check, Loader2, Wind } from "lucide-react";

const BREATHING = [
  { key: "box", label: "Box (4-4-4-4)" }, { key: "478", label: "4-7-8" }, { key: "coherent", label: "Coherent 5-5" },
  { key: "physiological_sigh", label: "Physiological Sigh" }, { key: "bhramari", label: "Bhramari" },
  { key: "nadi_shodhana", label: "Nadi Shodhana" }, { key: "ocean", label: "Ocean (Ujjayi)" },
  { key: "wim_hof", label: "Wim Hof" }, { key: "kapalabhati", label: "Kapalabhati" }, { key: "connected", label: "Connected" },
];
const DEEP = [
  { key: "goals", label: "My Goals" }, { key: "beliefs", label: "My Beliefs" }, { key: "upper_limits", label: "My Upper Limits" },
  { key: "comfortzone", label: "My Comfort Zone" }, { key: "coping", label: "My Coping" }, { key: "journal", label: "Journaling" },
];
const RDR = [
  { key: "inner_critic", label: "Inner critic (Realize)" }, { key: "body_scan", label: "Body scan (Realize)" },
  { key: "dream_log", label: "Dream log (Realize)" }, { key: "core_transformation", label: "Core transformation (Realize)" },
  { key: "parts_work", label: "Parts work (Designate)" }, { key: "emotion_wheel", label: "Emotion wheel (Designate)" },
  { key: "abc_model", label: "ABC model (Designate)" }, { key: "cognitive_restructuring", label: "Cognitive restructuring (Designate)" },
  { key: "the_work", label: "The Work (Release)" }, { key: "tre", label: "TRE (Release)" },
  { key: "emdr", label: "EMDR-inspired (Release)" }, { key: "connected_breath", label: "Connected breath (Release)" },
];
const CATALOG = { breathing: BREATHING, deep_within: DEEP, rdr: RDR };
const CATS = ["breathing", "deep_within", "rdr", "custom", "coach_method"];
const REMINDERS = [0, 1, 5, 10, 15, 30, 60];

function nextMondayIso() {
  const t = new Date();
  const until = (8 - t.getDay()) % 7 || 7;
  const m = new Date(t);
  m.setDate(t.getDate() + until);
  return m.toISOString().slice(0, 10);
}

function emptySlot() {
  return { day: 0, time: "09:00", duration_min: 10, category: "breathing", technique: "box", reminder_min_before: 5, note: "" };
}

export function WeeklyPlanEditorDialog({ open, planId, onClose }) {
  const t = useT();
  const DAYS = (t("pl.w_days") || "Mo,Tu,We,Th,Fr,Sa,Su").split(",");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [weekStart, setWeekStart] = useState(nextMondayIso());
  const [slots, setSlots] = useState([]);
  const [customTechs, setCustomTechs] = useState([]);
  const [methods, setMethods] = useState([]);
  const [draft, setDraft] = useState(null); // slot being added/edited
  const [draftIdx, setDraftIdx] = useState(-1);

  const load = useCallback(async () => {
    const [ct, ms] = await Promise.all([
      api.get("/coach/breathwork-techniques").then((r) => r.data).catch(() => []),
      api.get("/coach/methods").then((r) => r.data).catch(() => []),
    ]);
    setCustomTechs(Array.isArray(ct) ? ct : []);
    setMethods(Array.isArray(ms) ? ms : []);
    if (planId) {
      setLoading(true);
      try {
        const { data } = await api.get(`/breathwork/plans/${planId}`);
        setName(data.name || ""); setDescription(data.description || "");
        setWeekStart(data.week_start || nextMondayIso());
        setSlots(Array.isArray(data.slots) ? data.slots : []);
      } catch { toast.error(t("pl.w_save_failed")); } finally { setLoading(false); }
    } else {
      setName(t("pl.w_default_name")); setDescription(""); setWeekStart(nextMondayIso()); setSlots([]);
    }
  }, [planId, t]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const techLabel = (s) => {
    if (s.category === "custom") return s.custom_snapshot?.name || customTechs.find((c) => `custom:${c.id}` === s.technique)?.name || "Breathwork";
    if (s.category === "coach_method") { const id = (s.technique || "").replace("coach_method:", ""); return methods.find((m) => m.id === id)?.title || t("pl.w_cat_coach_method"); }
    return (CATALOG[s.category] || BREATHING).find((x) => x.key === s.technique)?.label || s.technique;
  };

  const techOptions = (cat) => {
    if (cat === "custom") return customTechs.map((c) => ({ key: `custom:${c.id}`, label: c.name, snap: c }));
    if (cat === "coach_method") return methods.map((m) => ({ key: `coach_method:${m.id}`, label: m.title }));
    return CATALOG[cat] || BREATHING;
  };

  const openSlot = (idx) => { setDraftIdx(idx); setDraft(idx >= 0 ? { ...slots[idx] } : emptySlot()); };
  const pickCategory = (cat) => {
    const opts = techOptions(cat);
    setDraft((d) => ({ ...d, category: cat, technique: opts[0]?.key || "", custom_snapshot: cat === "custom" ? snapOf(opts[0]) : null }));
  };
  const snapOf = (opt) => opt?.snap ? {
    name: opt.snap.name, inhale_s: opt.snap.inhale_s, hold_after_inhale_s: opt.snap.hold_after_inhale_s,
    exhale_s: opt.snap.exhale_s, hold_after_exhale_s: opt.snap.hold_after_exhale_s,
    repetitions: opt.snap.repetitions ?? null, duration_s: opt.snap.duration_s ?? null,
  } : null;
  const pickTechnique = (opt) => setDraft((d) => ({ ...d, technique: opt.key, custom_snapshot: d.category === "custom" ? snapOf(opt) : null }));

  const saveSlot = () => {
    if (!draft.technique) { toast.error(t("pl.w_need_tech")); return; }
    const clean = { ...draft, note: draft.note?.trim() || null };
    setSlots((prev) => { const n = [...prev]; if (draftIdx >= 0) n[draftIdx] = clean; else n.push(clean); return n.sort((a, b) => a.day - b.day || a.time.localeCompare(b.time)); });
    setDraft(null); setDraftIdx(-1);
  };

  const savePlan = async () => {
    if (name.trim().length < 1) { toast.error(t("pl.w_need_name")); return; }
    if (slots.length === 0) { toast.error(t("pl.w_need_slots")); return; }
    setSaving(true);
    const body = {
      name: name.trim(), description: description.trim() || null, week_start: weekStart || null,
      slots: slots.map((s) => ({
        day: s.day, time: s.time, duration_min: s.duration_min, technique: s.technique,
        category: s.category || "breathing", reminder_min_before: s.reminder_min_before ?? 5,
        note: s.note || null, ...(s.category === "custom" && s.custom_snapshot ? { custom_snapshot: s.custom_snapshot } : {}),
      })),
    };
    try {
      if (planId) { await api.patch(`/breathwork/plans/${planId}`, body); toast.success(t("pl.w_updated")); }
      else { await api.post("/breathwork/plans", body); toast.success(t("pl.w_created")); }
      onClose(true);
    } catch (err) { toast.error(apiError(err, t("pl.w_save_failed"))); }
    finally { setSaving(false); }
  };

  const reminderLabel = (m) => (m === 0 ? t("pl.w_rem_start") : m === 60 ? t("pl.w_rem_hour") : t("pl.w_rem_min", { n: m }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="pl-weekly-editor">
        <DialogHeader>
          <DialogTitle>{planId ? t("pl.w_editor_edit") : t("pl.w_editor_new")}</DialogTitle>
          <DialogDescription>{t("pl.w_editor_sub")}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("pl.w_name")}</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={140} placeholder={t("pl.w_name_ph")} data-testid="pl-w-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("pl.w_week_start")}</label><Input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} data-testid="pl-w-weekstart" /></div>
              <div><label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("pl.w_desc")}</label><Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} placeholder={t("pl.w_desc_ph")} data-testid="pl-w-desc" /></div>
            </div>

            {/* Slots */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("pl.w_slots_lbl")} ({slots.length})</label>
                {!draft && <Button type="button" size="sm" className="gap-1.5 rounded-full" onClick={() => openSlot(-1)} data-testid="pl-w-add-slot"><Plus className="h-3.5 w-3.5" /> {t("pl.w_add_slot")}</Button>}
              </div>

              {slots.length === 0 && !draft ? (
                <p className="rounded-xl border border-dashed border-border bg-card p-3 text-sm text-muted-foreground">{t("pl.w_no_slots")}</p>
              ) : (
                <div className="space-y-1.5">
                  {slots.map((s, i) => (
                    <div key={i} data-testid={`pl-w-slot-${i}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-2.5">
                      <div className="flex h-9 w-9 shrink-0 flex-col items-center justify-center rounded-lg bg-primary/10 text-[10px] font-bold uppercase text-primary">{DAYS[s.day]}</div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{techLabel(s)}</p>
                        <p className="text-xs text-muted-foreground"><Clock className="mr-0.5 inline h-3 w-3" />{s.time} · {s.duration_min} Min · {reminderLabel(s.reminder_min_before ?? 5)}</p>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => openSlot(i)} data-testid={`pl-w-slot-edit-${i}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-rose-500 hover:text-rose-600" onClick={() => setSlots((p) => p.filter((_, j) => j !== i))} data-testid={`pl-w-slot-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Slot editor panel */}
              {draft && (
                <div className="mt-3 space-y-3 rounded-2xl border border-primary/40 bg-primary/5 p-3" data-testid="pl-w-slot-panel">
                  <div>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("pl.w_slot_day")}</p>
                    <div className="flex flex-wrap gap-1">
                      {DAYS.map((d, di) => (
                        <button key={di} onClick={() => setDraft((x) => ({ ...x, day: di }))} data-testid={`pl-w-day-${di}`} className={`h-8 w-10 rounded-lg text-xs font-semibold ${draft.day === di ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{d}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><p className="mb-1 text-[11px] text-muted-foreground">{t("pl.w_slot_time")}</p><Input type="time" value={draft.time} onChange={(e) => setDraft((x) => ({ ...x, time: e.target.value }))} data-testid="pl-w-slot-time" /></div>
                    <div><p className="mb-1 text-[11px] text-muted-foreground">{t("pl.w_slot_duration")}</p><Input type="number" min="1" max="120" value={draft.duration_min} onChange={(e) => setDraft((x) => ({ ...x, duration_min: parseInt(e.target.value) || 1 }))} data-testid="pl-w-slot-duration" /></div>
                    <div>
                      <p className="mb-1 text-[11px] text-muted-foreground">{t("pl.w_slot_reminder")}</p>
                      <select value={draft.reminder_min_before ?? 5} onChange={(e) => setDraft((x) => ({ ...x, reminder_min_before: parseInt(e.target.value) }))} data-testid="pl-w-slot-reminder" className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                        {REMINDERS.map((m) => <option key={m} value={m}>{reminderLabel(m)}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("pl.w_slot_category")}</p>
                    <div className="flex flex-wrap gap-1">
                      {CATS.map((c) => (
                        <button key={c} onClick={() => pickCategory(c)} data-testid={`pl-w-cat-${c}`} className={`rounded-full px-2.5 py-1 text-xs font-medium ${draft.category === c ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{t(`pl.w_cat_${c}`)}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("pl.w_slot_technique")}</p>
                    {techOptions(draft.category).length === 0 ? (
                      <p className="rounded-lg border border-dashed border-border bg-card p-2 text-xs text-muted-foreground">{draft.category === "custom" ? t("pl.w_no_custom") : t("pl.w_no_methods")}</p>
                    ) : (
                      <div className="max-h-44 space-y-1 overflow-y-auto">
                        {techOptions(draft.category).map((o) => (
                          <button key={o.key} onClick={() => pickTechnique(o)} data-testid={`pl-w-tech-${o.key}`} className={`flex w-full items-center gap-2 rounded-lg border p-2 text-left text-sm ${draft.technique === o.key ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                            <Wind className="h-3.5 w-3.5 shrink-0 text-primary" /> <span className="truncate">{o.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Textarea className="min-h-[48px]" value={draft.note || ""} onChange={(e) => setDraft((x) => ({ ...x, note: e.target.value }))} maxLength={500} placeholder={t("pl.w_slot_note")} data-testid="pl-w-slot-note" />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" className="rounded-full" onClick={() => { setDraft(null); setDraftIdx(-1); }} data-testid="pl-w-slot-cancel">{t("common.cancel")}</Button>
                    <Button className="gap-1.5 rounded-full" onClick={saveSlot} disabled={!draft.technique} data-testid="pl-w-slot-save"><Check className="h-4 w-4" /> {t("pl.w_slot_save")}</Button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-border pt-3">
              <Button variant="outline" className="rounded-full" onClick={() => onClose(false)}>{t("common.cancel")}</Button>
              <Button className="rounded-full" onClick={savePlan} disabled={saving || !name.trim() || slots.length === 0} data-testid="pl-w-save-plan">{saving ? t("common.saving") : planId ? t("common.save") : t("pl.w_create_plan")}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
